/**
 * Customer Search Slice — search results only
 *
 * The drawer-tree page manages its own expansion state locally (a Set of node
 * IDs in component state). This slice no longer tracks "selected customer" or
 * "selected vehicle" — that's all derivable from local UI state, and trying
 * to centralise it just creates sync bugs.
 *
 * What lives here:
 *  - The current rego/name/phone search criteria
 *  - The result list (customers, each with their vehicles)
 *  - Loading/error/hasSearched flags
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { searchCustomers } from '../../services/customerApi';

// ============================================
// Async Thunks
// ============================================

export const searchCustomersThunk = createAsyncThunk(
    'customerSearch/search',
    async ({ name, phone, rego }, { rejectWithValue }) => {
        try {
            const response = await searchCustomers({ name, phone, rego });
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message || 'Failed to search customers.'
            );
        }
    }
);

// ============================================
// Slice
// ============================================

const initialState = {
    regoQuery:  '',
    nameQuery:  '',
    phoneQuery: '',

    customers: [],
    loading: false,
    error: null,
    hasSearched: false,
};

const customerSearchSlice = createSlice({
    name: 'customerSearch',
    initialState,
    reducers: {
        setRegoQuery:  (state, action) => { state.regoQuery  = action.payload; },
        setNameQuery:  (state, action) => { state.nameQuery  = action.payload; },
        setPhoneQuery: (state, action) => { state.phoneQuery = action.payload; },
        clearSearch:   () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(searchCustomersThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(searchCustomersThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.customers = action.payload;
                state.hasSearched = true;
            })
            .addCase(searchCustomersThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Search failed.';
                state.customers = [];
                state.hasSearched = true;
            });
    },
});

export const {
    setRegoQuery,
    setNameQuery,
    setPhoneQuery,
    clearSearch,
} = customerSearchSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectCustomerSearchState = (state) => state.customerSearch;
export const selectCustomers           = (state) => state.customerSearch.customers;

export default customerSearchSlice.reducer;